import React, { useCallback, useEffect, useRef } from 'react'
import { DocumentStatus, UserRole } from '@alucio/aws-beacon-amplify/src/models';
import { isFolderItemORM, isPageGroupORM } from 'src/types/typeguards';
import { useContent } from 'src/state/context/ContentProvider/ContentProvider';
import ROUTES, { DNARoute } from 'src/router/routeDef';
import { useHistory, useLocation, useParams } from 'react-router';
import useCurrentPage from 'src/components/DNA/hooks/useCurrentPage';
import { useDispatch } from 'react-redux';
import { useDocumentORM, useDocumentVersionORM } from 'src/state/redux/selector/document';
import { useCurrentUserORM } from 'src/state/redux/selector/user';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import { contentPreviewModalActions } from 'src/state/redux/slice/contentPreviewModal';
import ForbiddenModal from '../Model/Forbidden';
import { DNAButton, DNABox, DNAText, useToast } from '@alucio/lux-ui';
import DNAPopover from 'src/components/DNA/Popover/DNAPopover';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { copyDocPageUrlHandler } from 'src/utils/shareLink/shareLink.web';
import {
  canCopyDocumentLink,
  getLinkableDocumentVersion,
  shouldOpenPublisherMode,
} from 'src/utils/shareLink/documentPageLink';
import qs from 'qs';
import { useAppSettings } from 'src/state/context/AppSettings';

type validPagesIds =
  typeof ROUTES.LIBRARY.UID
  | typeof ROUTES.SEARCH_RESULTS.UID
  | typeof ROUTES.HOME.UID
  | typeof ROUTES.BOOKMARKS.UID

type RoutePageUrlType = {
  [x in validPagesIds]: () => string;
};

const DNA_ROUTE_PAGE_ID =
  (location, currentPage: DNARoute, docId: string, pageNumber: string) : RoutePageUrlType => ({
    [ROUTES.LIBRARY.UID]: () => {
      const search = location.search
      const replaceParams = ROUTES.BEACON_COPY_LINK.PATH
        .replace(':docId?', docId)
        .replace(':page?', pageNumber)

      const newUrl = `${replaceParams}${search}${location.hash}`;
      replaceUrlState(newUrl);
      return `${currentPage.PATH}${search}${location.hash}`
    },
    [ROUTES.HOME.UID]: () => {
      let search = location.search

      // if search contains ?code=, we want to remove it from the url due that comes from the sso and makes the copied url invalid
      // using qs check if the search contains the code parameter
      const searchWithoutCode = qs.parse(search, { ignoreQueryPrefix: true })
      if (searchWithoutCode.code) {
        delete searchWithoutCode.code
        search = qs.stringify(searchWithoutCode, { addQueryPrefix: true })
      }

      const replaceParams = ROUTES.BEACON_COPY_LINK_HOME.PATH
        .replace(':docId?', docId)
        .replace(':page?', pageNumber)

      const newUrl = `${replaceParams}${search}${location.hash}`;
      replaceUrlState(newUrl);

      return `${currentPage.PATH}${search}${location.hash}`
    },
    [ROUTES.BOOKMARKS.UID]: () => {
      const search = location.search
      const replaceParams = ROUTES.BEACON_COPY_LINK_BOOKMARKS.PATH
        .replace(':docId?', docId)
        .replace(':page?', pageNumber)

      const newUrl = `${replaceParams}${search}${location.hash}`;
      replaceUrlState(newUrl);
      return `${currentPage.PATH}${search}${location.hash}`
    },
    [ROUTES.SEARCH_RESULTS.UID]: () => {
      const search = location.search
      const searchText = location.pathname.split('/')[2]

      const replaceParams = currentPage.PATH
        .replace(':docId?', docId)
        .replace(':page?', pageNumber)
        .replace(':searchText', searchText)

      const newUrl = `${replaceParams}${search}${location.hash}`;
      replaceUrlState(newUrl);
      return `${currentPage.PATH.replace(':searchText', searchText)}${search}${location.hash}`
    },
  })

export const replaceUrlState = (newUrl: string | undefined) => {
  window.history.replaceState({}, '', newUrl)
}

export const useCPMPageRoute = () => {
  const { deviceMode } = useAppSettings()
  const dispatch = useDispatch()
  const { docId, page } = useParams<{ docId: string, page: string }>()
  const location = useLocation()
  const history = useHistory()
  const openedLocation = useRef<typeof location>()
  const doc = useDocumentORM(docId)
  const currentUser = useCurrentUserORM()
  const currentPage = useCurrentPage()
  const status = doc?.model.status
  const isPublisher = currentUser?.model.role === UserRole.TENANT_PUBLISHER
  const isValidStatus = status === DocumentStatus.PUBLISHED ||
    (isPublisher && status === DocumentStatus.NOT_PUBLISHED)
  const docVer = getLinkableDocumentVersion(
    doc?.relations.version.latestPublishedDocumentVersionORM,
    doc?.relations.version.latestDocumentVersionORM,
    isPublisher,
  )

  const onClosed  = useCallback(() => {
    if (!currentPage) return
    openedLocation.current = location
    replaceUrlState(removeEmptyParams(currentPage.PATH))
  }, [currentPage, location])

  useEffect(() => {
    // if the doc id si not a valid id, we don't want to open the modal
    if (!docId || docId.match(/:docId\?{0,1}/)) {
      openedLocation.current = undefined
      return
    }
    if (!currentPage) return
    // URL cleanup uses replaceState, so router params can outlive the preview.
    // Consume ORM refreshes and display-mode replacements, but allow explicit revisits.
    if (openedLocation.current === location) return
    if (openedLocation.current?.pathname === location.pathname && history.action === 'REPLACE') {
      openedLocation.current = location
      return
    }

    // on tablet we dont have the home page, so we don't want to open the modal
    if (currentPage.UID === 'HOME' && deviceMode === 'tablet') {
      return
    }

    if (docVer && page && isValidStatus) {
      openedLocation.current = location
      dispatch(
        contentPreviewModalActions.setModalVisibility({
          documentVersionId: docVer?.model.id,
          isOpen: true,
          content: docVer,
          fromEllipsis: true,
          isPublisherMode: shouldOpenPublisherMode(status, isPublisher),
          pageNumber: parseInt(page, 10),
        }),
      )
    }
    else {
      dispatch(
        DNAModalActions.setModal({
          isVisible: true,
          allowBackdropCancel: false,
          component: (props) => (<ForbiddenModal
            {...props}
            onClosed={onClosed}
          />),
        }))
    }
  }, [
    currentPage, deviceMode, dispatch, docId, docVer, history,
    isPublisher, isValidStatus, location, onClosed, page, status,
  ])
}

export const removeEmptyParams = (url: string | undefined) => {
  return url?.split('/')
    // used to remove the optional parameters that doesn't have a value those that start with : and end with ?
    .filter(element => !/^:.*\?$/.test(element))
    .join('/')
}

/**
 * This hook updates the URL with the current document and page information when the active presentation changes.
 * It relies on side effects to update the URL and remove any empty parameters.
 */
export const useAddDocAndPageToUrl = () => {
  const { activePresentation } = useContent()
  const location = useLocation()
  const currentPage = useCurrentPage()
  const [originalUrl, setOriginalUrl] = React.useState<string | null>(null)

  useEffect(() => {
    if (!activePresentation?.presentable?.orm) return
    if (isFolderItemORM(activePresentation?.presentable.orm)) return
    if (isPageGroupORM(activePresentation?.presentable.orm)) return
    if (!currentPage?.UID) return

    const orm = activePresentation.presentable.orm
    const docId = orm.model.documentId
    const pageNumber = activePresentation.currentPresentablePage.presentationPageNumber.toString()
    const pageRoutes = DNA_ROUTE_PAGE_ID(location, currentPage, docId, pageNumber)
    const prevUrl = pageRoutes[currentPage.UID]?.()

    // If we're in search page, store original URL and modify current URL
    if (location.pathname.startsWith('/search/')) {
      setOriginalUrl(window.location.href)
      const searchParams = new URLSearchParams(location.search)
      const newUrl = `${window.location.origin}/library/${docId}/${pageNumber}${
        searchParams.toString() ? `?${searchParams}` : ''
      }`
      replaceUrlState(newUrl)
    }

    return () => {
      // [NOTE] - Avoid rewriting the URL when on the Meeting History page, i.e. When CPM closes from starting a presentation
      //        - Unfortunately, there's a race condition so we cannot rely on normal state updates from the router and must look to window.location :(
      //        - Consider redesigning this to be able to control the flow better
      const isRedirectingToMeetings = window.location.pathname.includes(ROUTES.MEETING_PRESENTATION.UID)

      if (!isRedirectingToMeetings) {
        if (originalUrl) {
          replaceUrlState(originalUrl)
          setOriginalUrl(null)
        } else {
          // we want to remove from the url the optional parameters that doesn't have a value
          replaceUrlState(removeEmptyParams(prevUrl))
        }
      }
    }
  }, [activePresentation])
}

export const CopyCPMLink: React.FC<{
  documentVersionId: string
  pageNumber?: number
  useSecondaryButtonStyle?: boolean
  testID?: string
}> = (props) => {
  const toast = useToast()
  const docVersion = useDocumentVersionORM(props.documentVersionId)
  const doc = useDocumentORM(docVersion?.model.documentId)
  const docStatus = doc?.model.status
  const currentUser = useCurrentUserORM()
  const isPublisher = currentUser?.model.role === UserRole.TENANT_PUBLISHER
  const pageNumber = props.pageNumber ?? 1
  const useSecondaryButtonStyle = props.useSecondaryButtonStyle
  const linkableVersion = getLinkableDocumentVersion(
    doc?.relations.version.latestPublishedDocumentVersionORM,
    doc?.relations.version.latestDocumentVersionORM,
    isPublisher,
  )

  const copy = () => {
    if (!doc) return
    copyDocPageUrlHandler(toast, doc.model.id, pageNumber)
  }

  if (!canCopyDocumentLink(docStatus, isPublisher) || linkableVersion?.model.id !== props.documentVersionId) {
    return null
  }

  return (
    <DNAPopover>
      <DNAPopover.Anchor>
        <DNAButton
          testID={props.testID || 'copy-link-button'}
          appearance="outline"
          status={useSecondaryButtonStyle ? 'secondary' : 'tertiary'}
          size={useSecondaryButtonStyle ? 'md' : undefined}
          iconLeft="link"
          onPress={copy}
        />
      </DNAPopover.Anchor>
      <DNAPopover.Content offset={5}>
        <DNABox spacing="sm" appearance="col">
          <DNAText style={{ color: colors['color-text-basic'] }}>
            Copy link to clipboard
          </DNAText>
        </DNABox>
      </DNAPopover.Content>
    </DNAPopover>

  )
}
